import React from 'react';
import { Box, Stack, Tooltip, Typography, useTheme } from '@mui/material';

/**
 * Onde o dinheiro fica preso, em dias.
 *
 * Dois trilhos na mesma régua: em cima o caminho do produto (compra → venda →
 * recebimento), embaixo o prazo que o fornecedor concede. A diferença entre os
 * dois é o ciclo financeiro, e é ela que o dono financia todo mês.
 *
 * Ver os dois trilhos na mesma escala é o que transforma três prazos médios
 * soltos numa frase que o dono entende sem explicação.
 */
const LinhaDoTempoDoCiclo = ({ dados }) => {
  const theme = useTheme();
  if (!dados) return null;

  const largura = (dias) => `${Math.max(0, (dias / dados.escala) * 100)}%`;
  const neutro = theme.palette.mode === 'dark' ? '#4b5f78' : '#8fa3b8';

  return (
    <Box>
      <Stack spacing={1.5}>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Do estoque ao recebimento — {Math.round(dados.operacional)} dias
          </Typography>
          <Box sx={{ display: 'flex', height: 24, mt: 0.5, gap: '2px' }}>
            <Tooltip title={`Estoque parado: ${Math.round(dados.pmre)} dias`}>
              <Box
                sx={{
                  width: largura(dados.pmre),
                  bgcolor: neutro,
                  borderRadius: '4px 0 0 4px',
                  transition: 'width 300ms cubic-bezier(0.4, 0, 0.2, 1)',
                  '@media (prefers-reduced-motion: reduce)': { transition: 'none' }
                }}
              />
            </Tooltip>
            <Tooltip title={`Cliente pagando: ${Math.round(dados.pmrv)} dias`}>
              <Box
                sx={{
                  width: largura(dados.pmrv),
                  bgcolor: theme.palette.primary.main,
                  borderRadius: '0 4px 4px 0',
                  transition: 'width 300ms cubic-bezier(0.4, 0, 0.2, 1)',
                  '@media (prefers-reduced-motion: reduce)': { transition: 'none' }
                }}
              />
            </Tooltip>
          </Box>
        </Box>

        <Box>
          <Typography variant="caption" color="text.secondary">
            Prazo do fornecedor — {Math.round(dados.pmpc)} dias
          </Typography>
          <Box sx={{ display: 'flex', height: 24, mt: 0.5 }}>
            <Box
              sx={{
                width: largura(dados.pmpc),
                // PRAZO NÃO É SAÚDE. Esta barra mede dias de fornecedor, dentro
                // de um bloco cujo veredito pode ser Crítico — e ela saía verde,
                // dizendo "bom" sobre uma duração que não foi classificada.
                // Verde, âmbar e vermelho neste produto são faixa de saúde; o
                // que é categoria usa o acento.
                bgcolor: theme.palette.primary.main,
                borderRadius: 1,
                opacity: 0.75,
                transition: 'width 300ms cubic-bezier(0.4, 0, 0.2, 1)',
                '@media (prefers-reduced-motion: reduce)': { transition: 'none' }
              }}
            />
          </Box>
        </Box>
      </Stack>

      <Typography variant="body2" sx={{ mt: 2, maxWidth: '65ch' }}>
        {dados.financeiro > 0
          ? `Sobram ${Math.round(dados.financeiro)} dias que a empresa financia do próprio bolso.`
          : `O fornecedor financia ${Math.abs(Math.round(dados.financeiro))} dias do ciclo — a operação libera caixa.`}
      </Typography>
    </Box>
  );
};

export default LinhaDoTempoDoCiclo;
